/**
 * GMA-1504: the Silver Shield staff event pane listed every raw sensor event on
 * its own row, so two events that arrive a fraction of a second apart (e.g. a
 * door event and the occupancy event that follows it) showed up as two separate,
 * seemingly contradictory lines on the same clock second. Group events that fall
 * within a short window into a single timeline entry so a single moment of
 * activity reads as one row.
 *
 * Display-only: every underlying event is preserved on the returned group
 * (`events`) so the raw detail is still available, and the stored data is never
 * changed. This is the "group consecutive related events" option from the
 * ticket; richer occupancy-session aggregation (start/end/duration) is a
 * follow-up.
 */

const DEFAULT_WINDOW_MS = 1500;

function labelOf(ev) {
  return (ev && (ev.eventLabel || ev.eventType)) || "";
}

function timeMs(ev) {
  const t = new Date(ev?.eventAt).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Group staff events into timeline entries. Consecutive events whose timestamps
 * fall within `windowMs` of the group's newest event are merged. Output is
 * newest-first.
 *
 * @param {Array<object>} events - raw events ({ id, eventType, eventLabel, eventAt, sensorSn }).
 * @param {number} [windowMs] - merge window in milliseconds (default 1500).
 * @returns {Array<{key:string,eventAt:string,sensorSn:?string,events:Array,labels:string[],combinedLabel:string,count:number}>}
 */
export function groupStaffEvents(events, windowMs = DEFAULT_WINDOW_MS) {
  if (!Array.isArray(events) || events.length === 0) return [];

  // Defensive: don't assume caller order. Newest-first.
  const sorted = [...events].sort((a, b) => (timeMs(b) ?? 0) - (timeMs(a) ?? 0));

  const groups = [];
  for (const ev of sorted) {
    const t = timeMs(ev);
    const current = groups[groups.length - 1];
    // Join the current group when this (older) event is within `windowMs` of the
    // group's anchor (its newest event). Bounds each group's span to windowMs so
    // a long activity stream is not collapsed into one giant entry.
    if (current && t !== null && current.anchorMs !== null && current.anchorMs - t <= windowMs) {
      current.events.push(ev);
    } else {
      groups.push({ anchorMs: t, events: [ev] });
    }
  }

  return groups.map((g) => {
    // Combined label in chronological order (oldest -> newest), de-duplicated.
    const chrono = [...g.events].sort((a, b) => (timeMs(a) ?? 0) - (timeMs(b) ?? 0));
    const labels = [];
    for (const ev of chrono) {
      const l = labelOf(ev);
      if (l && !labels.includes(l)) labels.push(l);
    }
    const newest = g.events[0]; // groups are built from the newest-first list
    return {
      key: g.events.map((e) => e.id).join("-"),
      eventAt: newest.eventAt,
      sensorSn: newest.sensorSn || null,
      events: g.events,
      labels,
      combinedLabel: labels.join(" + "),
      count: g.events.length,
    };
  });
}

export default groupStaffEvents;
